import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Heart, MapPin, Calendar } from 'lucide-react';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const GuestWebsitePublic = () => {
  const { slug } = useParams();
  const [website, setWebsite] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWebsite();
  }, [slug]);

  const loadWebsite = async () => {
    try {
      const response = await axios.get(`${API}/guest-websites/slug/${slug}`);
      setWebsite(response.data);
    } catch (error) {
      console.error('Error loading website:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
        <div className="text-center">
          <Heart className="text-[#881337] mx-auto mb-4 animate-pulse" size={48} fill="#881337" />
          <p className="text-[#57534E]">Loading wedding details...</p>
        </div>
      </div>
    );
  }

  if (!website) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
        <div className="text-center">
          <h1 className="text-4xl font-serif text-[#1C1917] mb-4">Wedding Not Found</h1>
          <p className="text-[#57534E]">This wedding website doesn't exist or hasn't been published yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]" style={{ accentColor: website.theme_color }}>
      {/* Hero */}
      <section className="py-24 px-6 text-center">
        <Heart className="mx-auto mb-6" size={64} style={{ color: website.theme_color }} fill={website.theme_color} />
        <h1 className="text-5xl md:text-6xl font-serif font-light tracking-tight text-[#1C1917] mb-4">
          You're Invited
        </h1>
        {website.partner1_name && website.partner2_name && (
          <div className="text-2xl md:text-3xl font-serif text-[#1C1917] mb-6">
            {website.partner1_name} & {website.partner2_name}
          </div>
        )}
        <div className="text-xl text-[#57534E]">To celebrate their wedding</div>
      </section>

      {/* Couple Story */}
      {website.couple_story && (
        <section className="py-16 px-6 max-w-3xl mx-auto">
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-12 border border-white/50 shadow-xl">
            <h2 className="text-3xl font-serif text-center mb-8" style={{ color: website.theme_color }}>Our Story</h2>
            <p className="text-[#57534E] leading-relaxed whitespace-pre-wrap text-center">{website.couple_story}</p>
          </div>
        </section>
      )}

      {/* Details */}
      <section className="py-16 px-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {website.ceremony_details && (
            <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-8 border border-white/50 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${website.theme_color}20` }}>
                  <Calendar style={{ color: website.theme_color }} size={24} />
                </div>
                <h3 className="text-2xl font-serif" style={{ color: website.theme_color }}>Ceremony</h3>
              </div>
              <p className="text-[#57534E] leading-relaxed whitespace-pre-wrap">{website.ceremony_details}</p>
            </div>
          )}

          {website.reception_details && (
            <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-8 border border-white/50 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${website.theme_color}20` }}>
                  <MapPin style={{ color: website.theme_color }} size={24} />
                </div>
                <h3 className="text-2xl font-serif" style={{ color: website.theme_color }}>Reception</h3>
              </div>
              <p className="text-[#57534E] leading-relaxed whitespace-pre-wrap">{website.reception_details}</p>
            </div>
          )}
        </div>
      </section>

      {/* Custom Message */}
      {website.custom_message && (
        <section className="py-16 px-6 max-w-3xl mx-auto">
          <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-12 border border-white/50 shadow-xl text-center">
            <p className="text-xl font-accent text-[#57534E] leading-relaxed">"{website.custom_message}"</p>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 text-center border-t border-stone-200">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Heart style={{ color: website.theme_color }} size={20} fill={website.theme_color} />
          <span className="text-sm text-[#57534E]">Created with VowFlow</span>
        </div>
      </footer>
    </div>
  );
};

export default GuestWebsitePublic;